import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';

interface Stats {
  users: {
    total: number;
    active: number;
    pending: number;
    suspended: number;
    newThisWeek: number;
    newThisMonth: number;
    online: number;
  };
  tournaments: {
    total: number;
    active: number;
    completed: number;
    registration: number;
    avgPlayersPerTournament: number;
  };
  matches: {
    total: number;
    active: number;
    completed: number;
    live: number;
  };
  players: {
    uniqueTotal: number;
  };
  topUsers: { username: string; tournamentCount: number }[];
  generatedAt: string;
}

const card: React.CSSProperties = {
  background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '14px',
  padding: '1.25rem 1.5rem',
};

const statVal: React.CSSProperties = {
  fontFamily: 'Bebas Neue',
  fontSize: '2.8rem',
  lineHeight: 1,
  color: 'var(--gold)',
};

const statLabel: React.CSSProperties = {
  fontSize: '0.68rem',
  color: 'var(--chalk-dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginTop: '0.2rem',
};

function StatTile({ value, label, accent }: { value: number | string; label: string; accent?: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '0.75rem' }}>
      <div style={{ ...statVal, color: accent ?? 'var(--gold)' }}>{value}</div>
      <div style={statLabel}>{label}</div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: 'var(--gold)',
      letterSpacing: '0.06em', marginBottom: '0.75rem', marginTop: '1.5rem',
      display: 'flex', alignItems: 'center', gap: '0.5rem',
    }}>{children}</h2>
  );
}

export default function AdminStatsPage() {
  const { getAccessToken } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getAccessToken();
      const res = await fetch('/api/admin/stats', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json() as Stats & { error?: string };
      if (!res.ok) throw new Error((data as { error?: string }).error ?? 'Failed');
      setStats(data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: '2.2rem', color: 'var(--gold)', lineHeight: 1, margin: 0 }}>
            📊 Platform Stats
          </h1>
          {lastRefresh && (
            <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', marginTop: '0.25rem' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            background: 'rgba(201,168,76,0.12)', color: 'var(--gold)',
            border: '1px solid rgba(201,168,76,0.3)', borderRadius: '8px',
            padding: '0.5rem 1.25rem', fontSize: '0.82rem', fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '10px', padding: '1rem', color: '#e74c3c', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {loading && !stats && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>{t.adminLoadingStats}</div>
      )}

      {stats && (
        <>
          {/* ── Live now ── */}
          <div style={{
            ...card,
            border: '1px solid rgba(46,204,113,0.3)',
            background: 'linear-gradient(135deg, rgba(46,204,113,0.07), rgba(255,255,255,0.02))',
            marginBottom: '0.5rem',
          }}>
            <div style={{ fontSize: '0.65rem', color: '#2ecc71', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.5rem', fontWeight: 600 }}>
              ● Live right now
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
              <StatTile value={stats.matches.live} label="Active match timers" accent="#2ecc71" />
              <StatTile value={stats.matches.active} label="Matches in progress" accent="#2ecc71" />
              <StatTile value={stats.tournaments.active} label="Tournaments active" accent="#2ecc71" />
              <StatTile value={stats.tournaments.registration} label="In registration" accent="#e67e22" />
            </div>
          </div>

          {/* ── Users ── */}
          <SectionHeader>👤 Users</SectionHeader>
          <div style={{ ...card }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
              <StatTile value={stats.users.online} label="Online now (5 min)" accent="#2ecc71" />
              <StatTile value={stats.users.total} label="Total accounts" />
              <StatTile value={stats.users.active} label="Active" accent="#2ecc71" />
              <StatTile value={stats.users.pending} label="Awaiting verification" accent="#e67e22" />
              <StatTile value={stats.users.suspended} label="Suspended" accent="#e74c3c" />
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
              <StatTile value={`+${stats.users.newThisWeek}`} label="New this week" accent="var(--gold)" />
              <StatTile value={`+${stats.users.newThisMonth}`} label="New this month" accent="var(--gold)" />
            </div>
          </div>

          {/* ── Tournaments ── */}
          <SectionHeader>🏆 Tournaments</SectionHeader>
          <div style={{ ...card }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
              <StatTile value={stats.tournaments.total} label="Total ever" />
              <StatTile value={stats.tournaments.completed} label="Completed" accent="#2ecc71" />
              <StatTile value={stats.tournaments.avgPlayersPerTournament} label="Avg players / tournament" />
              <StatTile value={stats.players.uniqueTotal} label="Unique player entries" accent="var(--gold)" />
            </div>
          </div>

          {/* ── Matches ── */}
          <SectionHeader>🔴 Matches</SectionHeader>
          <div style={{ ...card }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
              <StatTile value={stats.matches.total} label="Total ever" />
              <StatTile value={stats.matches.completed} label="Completed" accent="#2ecc71" />
              <StatTile value={stats.matches.active} label="In progress" accent="#e67e22" />
            </div>
          </div>

          {/* ── Top users ── */}
          {stats.topUsers.length > 0 && (
            <>
              <SectionHeader>👑 Most Active Organisers</SectionHeader>
              <div style={{ ...card }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                  {stats.topUsers.map((u, i) => (
                    <div key={u.username} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.55rem 0.75rem',
                      background: i === 0 ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.02)',
                      borderRadius: '8px',
                      border: `1px solid ${i === 0 ? 'rgba(201,168,76,0.25)' : 'rgba(255,255,255,0.05)'}`,
                    }}>
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.2rem', color: i === 0 ? 'var(--gold)' : 'var(--chalk-dim)', minWidth: '24px' }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </span>
                      <span style={{ flex: 1, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? 'var(--chalk)' : 'var(--chalk-dim)' }}>
                        {u.username}
                      </span>
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: i === 0 ? 'var(--gold)' : 'var(--chalk)', letterSpacing: '0.04em' }}>
                        {u.tournamentCount}
                      </span>
                      <span style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        tournaments
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textAlign: 'center', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
            Stats generated at {new Date(stats.generatedAt).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
}
